import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getTemplate,
  listTemplates,
  renderTemplate,
} from '@/services/templates';
import {
  ICreateTemplatePayload,
  IUpdateTemplatePayload,
  IListTemplatesParams,
  IRenderTemplatePayload,
} from '@/types/templates';
import { handleShowError } from '@/utils';
import { toast } from 'react-toastify';

const QUERY_KEYS = {
  TEMPLATES: 'templates',
  TEMPLATE: 'template',
};

export const useListTemplates = (params?: IListTemplatesParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TEMPLATES, params],
    queryFn: () => listTemplates(params),
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Always refetch on mount
  });
};

const useGetTemplate = (templateId: string, enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TEMPLATE, templateId],
    queryFn: () => getTemplate(templateId),
    enabled: enabled && !!templateId,
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICreateTemplatePayload) => createTemplate(payload),
    onSuccess: (newTemplate) => {
      // Add the new template to the existing list optimistically
      queryClient.setQueryData([QUERY_KEYS.TEMPLATES], (oldData: any) => {
        if (!oldData) return [newTemplate];
        return [newTemplate, ...oldData];
      });

      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    },
    onError: (error) => {
      handleShowError(error);
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: IUpdateTemplatePayload;
    }) => updateTemplate(templateId, payload),
    onMutate: async ({ templateId, payload }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });

      // Snapshot the previous value
      const previousTemplates = queryClient.getQueryData([QUERY_KEYS.TEMPLATES]);

      // Optimistically update to the new value
      queryClient.setQueryData([QUERY_KEYS.TEMPLATES], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.map((template: any) =>
          template.template_id === templateId ? { ...template, ...payload } : template,
        );
      });

      // Return a context object with the snapshotted value
      return { previousTemplates };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousTemplates) {
        queryClient.setQueryData([QUERY_KEYS.TEMPLATES], context.previousTemplates);
      }
      handleShowError(err);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => deleteTemplate(templateId),
    onMutate: async (templateId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });

      // Snapshot the previous value
      const previousTemplates = queryClient.getQueryData([QUERY_KEYS.TEMPLATES]);

      // Optimistically remove the template
      queryClient.setQueryData([QUERY_KEYS.TEMPLATES], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((template: any) => template.template_id !== templateId);
      });

      return { previousTemplates };
    },
    onError: (err, templateId, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousTemplates) {
        queryClient.setQueryData([QUERY_KEYS.TEMPLATES], context.previousTemplates);
      }
      handleShowError(err);
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    },
  });
};

const useRenderTemplate = () => {
  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: IRenderTemplatePayload;
    }) => renderTemplate(templateId, payload),
    onError: (error) => {
      handleShowError(error);
    },
  });
};
